import { useSelector } from 'react-redux'
import { type StoreState } from 'client/store'
import { type ConfigState } from 'client/reducers/configReducer'
import type { entities } from 'client/constants/entities'
import { type LanguageCode } from 'common_packages/assets/js/types/locale'
import type { puzzlesClientIds } from '../constants/puzzlesClientIds'

const LIGHT_TRIAL_SUFFIX = '-lighttrial'

type EntityName = typeof entities[number]

type PuzzlesClientId = typeof puzzlesClientIds[number]

export type EntitySettings = {
  id: number
  name: EntityName
  publicUrl: string
  platformUrl: string
  locale: string // [ISO 639-1]-[ISO 3166-1], eg "sv-SE"
  language: LanguageCode // ISO 639-1
  currency: CurrencySettings
  sso: SSOSettings
  defaultCountry: CountrySettings
  tags: Tag[]
  showLockedPuzzlesExpanded: boolean
  puzzlesClientId: PuzzlesClientId
  puzzlePacksClientId: string
}

export type Tag = {
  id: number
  tagLangKey: string
}

export type CurrencySettings = {
  id: number
  currencyCode: string
  name: string
}

export type CountrySettings = {
  id: number
  code: string
  vatPercentage: string
  name: string
}

export type SSOSettings = {
  signUpSignInPolicy: string
  domain: string
  clientId: string
  instance: string
}

export type Config = {
  isLightTrialEnabled: boolean
  lightTrialSuffix: string
  lightTrialDays: number
  lightTrialDaysText: string
  extendedTrialDays: number
  extendedTrialDaysText: string
  entity: EntitySettings
}

/**
 * This hook returns the configuration settings currently set in the admin
 */
const useConfig = (): Config => {
  const config = useSelector<StoreState, ConfigState | null>((store) => store.config)

  if (!config || !config.entity) {
    return {
      isLightTrialEnabled: false,
      lightTrialDays: undefined,
      lightTrialDaysText: '',
      lightTrialSuffix: '',
      extendedTrialDays: undefined,
      extendedTrialDaysText: undefined,
      entity: undefined,
    }
  }

  return {
    isLightTrialEnabled: config.isFreeTrialMode,
    lightTrialDays: config.freeTrialDays,
    lightTrialDaysText: `${config.freeTrialDays ?? ''}`,
    lightTrialSuffix: config.isFreeTrialMode ? LIGHT_TRIAL_SUFFIX : '',
    extendedTrialDays: config.extendedTrialDays,
    extendedTrialDaysText: `${config.extendedTrialDays ?? ''}`,
    entity: config.entity,
  }
}
export default useConfig
